import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { from, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { CoachCalendarService } from './coach-calendar.service';
import { IRescheduleRequest } from '../models/appointment.model';
import {
  RejectRescheduleModalComponent
} from '../../views/agenda/reject-reschedule-modal/reject-reschedule-modal.component';

/**
 * Answering a student's request to move a meeting. The agenda card and the "all requests" dialog
 * both offer it, so the rules live here once: approving asks first, rejecting collects a note.
 *
 * Whoever resolves a request announces it on `changed$`; every list showing requests — and the
 * day's plan, whose meeting just moved — listens and reloads.
 */
@Injectable({providedIn: 'root'})
export class CoachRescheduleService {
  private readonly changedSubject = new Subject<void>();

  /** Emits after a request was approved or rejected. */
  readonly changed$: Observable<void> = this.changedSubject.asObservable();

  constructor(
    private calendarService: CoachCalendarService,
    private modalService: NgbModal,
    private translate: TranslateService,
    private toastrService: ToastrService,
  ) {
  }

  /**
   * Approving moves the real meeting: the Google event is rewritten and the student is notified,
   * and none of that is undone by clicking again — hence the confirmation naming both times.
   */
  approve(request: IRescheduleRequest): Observable<boolean> {
    return from(this.confirmApprove(request))
      .pipe(
        switchMap(isConfirmed => {
          if (!isConfirmed) {
            return of(false);
          }

          return this.calendarService.approveRescheduleRequest(request.id)
            .pipe(
              tap(() => this.toastrService.success(this.translate.instant('agenda.reschedule_approved'))),
              map(() => true),
            );
        }),
        tap(isDone => isDone && this.changedSubject.next()),
      );
  }

  /** The note travels to the student, which is what lets them propose another time. */
  reject(request: IRescheduleRequest): Observable<boolean> {
    const modalRef = this.modalService.open(RejectRescheduleModalComponent, {
      size: 'md',
      centered: true,
      animation: true,
    });
    modalRef.componentInstance.request = request;

    return from(modalRef.closed)
      .pipe(
        tap(() => {
          this.toastrService.success(this.translate.instant('agenda.reschedule_rejected'));
          this.changedSubject.next();
        }),
        map(() => true),
      );
  }

  private async confirmApprove(request: IRescheduleRequest): Promise<boolean> {
    const Swal = (await import('sweetalert2')).default;
    const result = await Swal.fire({
      title: this.translate.instant('agenda.approve_reschedule'),
      text: this.translate.instant('agenda.approve_reschedule_confirm', {
        student: request.appointment?.student?.name ?? '',
        current: this.readableDate(request.appointment?.startsAt),
        requested: this.readableDate(request.requestedStartsAt),
      }),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('buttons.approve'),
      cancelButtonText: this.translate.instant('buttons.cancel_l'),
      heightAuto: false,
    });

    return !!result.isConfirmed;
  }

  /** The dialog is plain text, so the dates are formatted here rather than by a template pipe. */
  private readableDate(value?: string | null): string {
    return value ? formatDate(value, 'd MMMM, HH:mm', 'tr') : '';
  }
}
